import React, { useState, useEffect } from 'react';
import { Button, Input, Textarea, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, useDisclosure } from '@nextui-org/react';
import { FiPlus, FiEdit3, FiTrash2, FiCheck, FiX } from 'react-icons/fi';
import { format, addDays, subDays, isToday, isYesterday } from 'date-fns';
import { tr } from 'date-fns/locale';

const RoutinesComponent = ({ projectId, projectData }) => {
  const [routines, setRoutines] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [editingRoutine, setEditingRoutine] = useState(null);
  const [newRoutine, setNewRoutine] = useState({ title: '', description: '' });
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  const isAdmin = projectData?.userstatus === 11;

  // Mock data - gerçek API'den gelecek
  useEffect(() => {
    setRoutines([
      {
        id: 1,
        title: 'Günlük Standup Toplantısı',
        description: 'Takım üyeleri ile günlük durum değerlendirmesi',
        created_by: 1,
        created_at: '2024-01-01',
        completions: {
          '2024-01-15': [1, 2, 3], // User ID'leri
          '2024-01-14': [1, 2],
          '2024-01-13': [1, 2, 3, 4]
        }
      },
      {
        id: 2,
        title: 'Kod Review',
        description: 'Günlük kod inceleme ve değerlendirme',
        created_by: 1,
        created_at: '2024-01-01',
        completions: {
          '2024-01-15': [1, 3],
          '2024-01-14': [1, 2, 3, 4],
          '2024-01-13': [2, 3]
        }
      },
      {
        id: 3,
        title: 'Dokümantasyon Güncelleme',
        description: 'Proje dokümantasyonunu güncel tutma',
        created_by: 1,
        created_at: '2024-01-01',
        completions: {
          '2024-01-15': [1, 2],
          '2024-01-14': [1, 2, 3],
          '2024-01-13': [1, 2, 3, 4]
        }
      }
    ]);
  }, []);

  const formatDate = (date) => {
    return format(date, 'dd MMMM yyyy', { locale: tr });
  };

  const getDateKey = (date) => {
    return format(date, 'yyyy-MM-dd');
  };

  const isRoutineCompleted = (routineId, userId = 1) => {
    const dateKey = getDateKey(selectedDate);
    return routines.find(r => r.id === routineId)?.completions[dateKey]?.includes(userId) || false;
  };

  const toggleRoutineCompletion = (routineId, userId = 1) => {
    if (!isAdmin) {
      const dateKey = getDateKey(selectedDate);
      setRoutines(prev => prev.map(routine => {
        if (routine.id === routineId) {
          const completions = { ...routine.completions };
          if (!completions[dateKey]) {
            completions[dateKey] = [];
          }
          
          if (completions[dateKey].includes(userId)) {
            completions[dateKey] = completions[dateKey].filter(id => id !== userId);
          } else {
            completions[dateKey] = [...completions[dateKey], userId];
          }
          
          return { ...routine, completions };
        }
        return routine;
      }));
    }
  };

  const handleCreateRoutine = () => {
    if (newRoutine.title.trim()) {
      const routine = {
        id: Date.now(),
        title: newRoutine.title,
        description: newRoutine.description,
        created_by: 1,
        created_at: new Date().toISOString(),
        completions: {}
      };
      setRoutines(prev => [...prev, routine]);
      setNewRoutine({ title: '', description: '' });
      onClose();
    }
  };

  const handleEditRoutine = (routine) => {
    setEditingRoutine(routine);
    setNewRoutine({ title: routine.title, description: routine.description });
    onOpen();
  };

  const handleUpdateRoutine = () => {
    if (editingRoutine && newRoutine.title.trim()) {
      setRoutines(prev => prev.map(r => 
        r.id === editingRoutine.id 
          ? { ...r, title: newRoutine.title, description: newRoutine.description }
          : r
      ));
      setEditingRoutine(null);
      setNewRoutine({ title: '', description: '' });
      onClose();
    }
  };

  const handleDeleteRoutine = (routineId) => {
    if (window.confirm('Bu rutini silmek istediğinizden emin misiniz?')) {
      setRoutines(prev => prev.filter(r => r.id !== routineId));
    }
  };

  const navigateDate = (direction) => {
    if (direction === 'prev') {
      setSelectedDate(prev => subDays(prev, 1));
    } else {
      setSelectedDate(prev => addDays(prev, 1));
    }
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-dark-300">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Günlük Rutinler</h2>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
              Takım üyelerinin günlük rutinlerini takip edin
            </p>
          </div>
          
          {isAdmin && (
            <Button
              onClick={onOpen}
              size="sm"
              className="bg-blue-600 hover:bg-blue-700 text-white"
              startContent={<FiPlus size={14} />}
            >
              Yeni Rutin
            </Button>
          )}
        </div>
      </div>

      {/* Date Navigation */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-600 bg-white dark:bg-dark-300">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => navigateDate('prev')}
              className="text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600"
            >
              ←
            </Button>
            
            <div className="text-center">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                {formatDate(selectedDate)}
              </h3>
              <div className="flex items-center justify-center space-x-2 mt-1">
                {isToday(selectedDate) && (
                  <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 px-2 py-0.5 rounded">
                    Bugün
                  </span>
                )}
                {isYesterday(selectedDate) && (
                  <span className="text-xs bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 px-2 py-0.5 rounded">
                    Dün
                  </span>
                )}
              </div>
            </div>
            
            <Button
              size="sm"
              variant="ghost"
              onClick={() => navigateDate('next')}
              className="text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600"
            >
              →
            </Button>
          </div>
          
          <Button
            size="sm"
            variant="ghost"
            onClick={goToToday}
            className="text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20"
          >
            Bugün
          </Button>
        </div>
      </div>

      {/* Routines Table */}
      <div className="flex-1 overflow-y-auto bg-white dark:bg-dark-300">
        {routines.length > 0 ? (
          <div className="min-w-full">
            {/* Table Header */}
            <div className="bg-gray-50 dark:bg-dark-300 border-b border-gray-200 dark:border-gray-600 sticky top-0 z-10">
              <div className="grid grid-cols-12 gap-4 px-4 py-3 text-xs font-medium text-gray-700 dark:text-gray-300">
                <div className="col-span-1">#</div>
                <div className="col-span-3">Rutin Adı</div>
                <div className="col-span-3">Açıklama</div>
                <div className="col-span-2">Durum</div>
                <div className="col-span-2">İşlemler</div>
                <div className="col-span-1 text-center">✓</div>
              </div>
            </div>

            {/* Table Body */}
            <div className="divide-y divide-gray-200 dark:divide-gray-600">
              {routines.map((routine, index) => (
                <div
                  key={routine.id}
                  className="bg-white dark:bg-dark-300 hover:bg-gray-50 dark:hover:bg-gray-600/50 transition-colors"
                >
                  <div className="grid grid-cols-12 gap-4 px-4 py-3 text-sm">
                    {/* Sıra No */}
                    <div className="col-span-1 flex items-center text-gray-500 dark:text-gray-400">
                      {index + 1}
                    </div>

                    {/* Rutin Adı */}
                    <div className="col-span-3 flex items-center">
                      <span className="font-medium text-gray-900 dark:text-white truncate">
                        {routine.title}
                      </span>
                    </div>

                    {/* Açıklama */}
                    <div className="col-span-3 flex items-center">
                      <span className="text-gray-600 dark:text-gray-300 truncate">
                        {routine.description || '-'}
                      </span>
                    </div>

                    {/* Durum */}
                    <div className="col-span-2 flex items-center">
                      <Button
                        size="sm"
                        onClick={() => toggleRoutineCompletion(routine.id)}
                        disabled={isAdmin}
                        className={`w-full text-xs ${
                          isRoutineCompleted(routine.id)
                            ? 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-200 hover:bg-green-200 dark:hover:bg-green-900/60 border border-green-200 dark:border-green-600'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600'
                        }`}
                        startContent={
                          isRoutineCompleted(routine.id) ? <FiCheck size={12} /> : <FiX size={12} />
                        }
                      >
                        {isRoutineCompleted(routine.id) ? 'Tamamlandı' : 'Bekliyor'}
                      </Button>
                    </div>

                    {/* İşlemler */}
                    <div className="col-span-2 flex items-center space-x-1">
                      {isAdmin && (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEditRoutine(routine)}
                            className="text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 p-1"
                          >
                            <FiEdit3 size={12} />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteRoutine(routine.id)}
                            className="text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 p-1"
                          >
                            <FiTrash2 size={12} />
                          </Button>
                        </>
                      )}
                    </div>

                    {/* Hızlı İşaretleme */}
                    <div className="col-span-1 flex items-center justify-center">
                      <button
                        onClick={() => toggleRoutineCompletion(routine.id)}
                        disabled={isAdmin}
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                          isRoutineCompleted(routine.id)
                            ? 'bg-green-500 dark:bg-green-600 border-green-500 dark:border-green-600 text-white'
                            : 'bg-white dark:bg-dark-200 border-gray-300 dark:border-gray-500 hover:border-green-400 dark:hover:border-green-500 hover:bg-green-50 dark:hover:bg-green-900/20'
                        } ${isAdmin ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        {isRoutineCompleted(routine.id) && <FiCheck size={12} />}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-gray-400 dark:text-gray-600 mb-4">
              <FiPlus size={32} className="mx-auto" />
            </div>
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
              Henüz rutin yok
            </h3>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              {isAdmin 
                ? 'İlk rutini oluşturmak için "Yeni Rutin" butonuna tıklayın'
                : 'Admin tarafından rutin oluşturulmasını bekleyin'
              }
            </p>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalContent>
          <ModalHeader>
            {editingRoutine ? 'Rutini Düzenle' : 'Yeni Rutin Oluştur'}
          </ModalHeader>
          <ModalBody>
            <div className="space-y-4">
              <Input
                label="Rutin Başlığı"
                placeholder="Rutin başlığını girin"
                value={newRoutine.title}
                onChange={(e) => setNewRoutine(prev => ({ ...prev, title: e.target.value }))}
                variant="bordered"
              />
              <Textarea
                label="Açıklama"
                placeholder="Rutin açıklamasını girin (opsiyonel)"
                value={newRoutine.description}
                onChange={(e) => setNewRoutine(prev => ({ ...prev, description: e.target.value }))}
                variant="bordered"
                minRows={3}
              />
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onPress={onClose}>
              İptal
            </Button>
            <Button
              color="primary"
              onPress={editingRoutine ? handleUpdateRoutine : handleCreateRoutine}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {editingRoutine ? 'Güncelle' : 'Oluştur'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default RoutinesComponent;
